import { useAbility } from "@casl/react";
import { type FC, type ReactNode, useState } from "react";

import type { AppAbility } from "@recobra/core";
import { m } from "@recobra/i18n/messages";
import type { DocumentType } from "@recobra/schemas/document";
import { DocumentTypeSchema, isVideoMimeType } from "@recobra/schemas/document";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@recobra/ui/accordion";
import {
  Attachment,
  AttachmentContent,
  AttachmentDescription,
  AttachmentMedia,
  AttachmentTitle,
  AttachmentTrigger,
} from "@recobra/ui/attachment";

import type { DisplayDocument } from "./DisplayDocument";
import {
  attachmentStateMedia,
  getAttachmentDescription,
  getAttachmentState,
  getDocumentType,
} from "./UploadAttachmentState";
import { UploadEntryActions } from "./UploadEntryActions";
import type { UploadRow } from "./UploadTypes";
import { useDocumentViewer } from "./useDocumentViewer";

const PROCESSING_STATUSES = new Set<DisplayDocument["status"]>([
  "idle",
  "requesting-url",
  "uploading",
  "pending",
  "classifying",
]);

const VIEWABLE_STATUSES = new Set<DisplayDocument["status"]>([
  "pending",
  "uploaded",
  "classifying",
  "classified",
  "failed",
]);

export const UploadEntryList: FC<{
  rows: UploadRow[];
  defaultOpenSections?: "priority" | "all" | "none";
  showAnalysisDetails?: boolean;
  groupByType?: boolean;
  onRemove?: (row: UploadRow) => void;
  removeAllowed?: (row: UploadRow) => boolean;
  removeDisabled?: (row: UploadRow) => boolean;
  onRetry?: (row: UploadRow) => void;
  onOverride?: (row: UploadRow, documentType: DocumentType) => void;
  overridePending?: boolean;
}> = ({
  rows,
  defaultOpenSections = "priority",
  showAnalysisDetails,
  groupByType,
  onRemove,
  removeAllowed,
  removeDisabled,
  onRetry,
  onOverride,
  overridePending,
}) => {
  const [openSections, setOpenSections] = useState<string[] | null>(null);
  const openDocument = useDocumentViewer();
  const ability = useAbility<AppAbility>();
  const canViewVideoAnalysis = ability.can("readAnalysis", "Document");

  const renderCard = (row: UploadRow) => {
    const doc = row.doc;
    const attachmentState = getAttachmentState(doc, showAnalysisDetails);
    const openViewer = () => {
      if (!doc.viewUrl) return;

      void openDocument({
        documentId: doc.id,
        windowName: `document-viewer-${doc.filename}`,
      });
    };

    const downloadDocument = () => {
      if (!doc.viewUrl) return;

      void openDocument({
        documentId: doc.id,
        windowName: `document-download-${doc.filename}`,
      });
    };

    const canOpenViewer =
      Boolean(doc.viewUrl) &&
      isDocumentViewable(doc.status) &&
      (!isVideoMimeType(doc.mimeType) || browserCanPlay(doc.mimeType));
    const canDownload =
      Boolean(doc.viewUrl) &&
      isDocumentViewable(doc.status) &&
      isVideoMimeType(doc.mimeType) &&
      !canOpenViewer;

    return (
      <li key={row.id}>
        <Attachment state={attachmentState} size="sm" className="w-full">
          <AttachmentMedia variant={doc.thumbnailUrl ? "image" : "icon"}>
            {doc.thumbnailUrl ? (
              <img src={doc.thumbnailUrl} alt={doc.filename} loading="lazy" />
            ) : (
              attachmentStateMedia[attachmentState]
            )}
          </AttachmentMedia>
          <AttachmentContent>
            <AttachmentTitle>{doc.filename}</AttachmentTitle>
            <AttachmentDescription>
              {getAttachmentDescription(doc, showAnalysisDetails)}
            </AttachmentDescription>
          </AttachmentContent>
          <UploadEntryActions
            row={row}
            showAnalysisDetails={showAnalysisDetails}
            showVideoAnalysis={canViewVideoAnalysis}
            onDownload={canDownload ? downloadDocument : undefined}
            onRemove={onRemove}
            removeAllowed={removeAllowed}
            removeDisabled={removeDisabled}
            onRetry={onRetry}
            onOverride={onOverride}
            overridePending={overridePending}
          />
          {canOpenViewer && (
            <AttachmentTrigger aria-label={m.doc_list_view()} onClick={openViewer} />
          )}
        </Attachment>
      </li>
    );
  };

  if (!groupByType) {
    return <ul className="flex flex-col gap-1.5 px-px py-0.5">{rows.map(renderCard)}</ul>;
  }

  const sections = buildSections(rows);
  const defaultOpenValues = defaultOpenSectionsOf(sections, defaultOpenSections);
  const accordionValue = openSections ?? defaultOpenValues.map(({ value }) => value);

  return (
    <div className="flex flex-col gap-4">
      {rows.length > 0 && (
        <Accordion multiple value={accordionValue} onValueChange={setOpenSections}>
          {sections.map((section) =>
            renderAccordionSection({
              ...section,
              children: (
                <ul className="flex flex-col gap-1.5 px-px py-0.5">
                  {section.rows.map(renderCard)}
                </ul>
              ),
            }),
          )}
        </Accordion>
      )}
    </div>
  );
};

const PRIORITY_SECTION_VALUES = new Set(["processing", "warnings", "failed"]);

const isPrioritySection = (section: ReturnType<typeof buildSections>[number]) =>
  PRIORITY_SECTION_VALUES.has(section.value) || section.rows.some(({ doc }) => doc.isProblematic);

const buildSections = (rows: UploadRow[]) => [
  ...buildSection(
    "processing",
    m.doc_group_processing(),
    rows.filter(({ doc }) => PROCESSING_STATUSES.has(doc.status)),
  ),
  ...buildSection(
    "uploaded",
    m.doc_status_uploaded(),
    rows.filter(({ doc }) => doc.status === "uploaded"),
  ),
  ...buildSection(
    "warnings",
    m.doc_group_warnings(),
    rows.filter(({ doc }) => doc.status === "failed"),
  ),
  ...buildSection(
    "failed",
    m.doc_group_failed(),
    rows.filter(({ doc }) => doc.status === "upload-error"),
  ),
  ...buildTypeGroups(rows.filter(({ doc }) => doc.status === "classified")).map(
    ({ type, rows }) => ({
      value: type,
      title: m.document_type_label({ documentType: type }),
      count: rows.length,
      rows,
    }),
  ),
];

const buildSection = (value: string, title: string, rows: UploadRow[]) =>
  rows.length > 0 ? [{ value, title, count: rows.length, rows }] : [];

const buildTypeGroups = (classifiedRows: UploadRow[]) => {
  const typeGroupMap = new Map<DocumentType, UploadRow[]>();
  for (const row of classifiedRows) {
    const key = getDocumentType(row.doc) ?? "other";
    const existing = typeGroupMap.get(key);
    if (existing) existing.push(row);
    else typeGroupMap.set(key, [row]);
  }

  return DocumentTypeSchema.options
    .filter((type) => typeGroupMap.has(type))
    .map((type) => ({ type, rows: typeGroupMap.get(type) ?? [] }));
};

const renderAccordionSection = ({
  value,
  title,
  count,
  children,
}: {
  value: string;
  title: string;
  count: number;
  children: ReactNode;
}) => (
  <AccordionItem key={value} value={value}>
    <AccordionTrigger className="py-2 text-xs uppercase tracking-wide text-muted-foreground hover:no-underline">
      <span>
        {title}
        <span className="ml-1.5 font-normal opacity-70">({count})</span>
      </span>
    </AccordionTrigger>
    <AccordionContent className="pb-2 [&_p:not(:last-child)]:mb-0">{children}</AccordionContent>
  </AccordionItem>
);

const isDocumentViewable = (status: DisplayDocument["status"]): boolean =>
  VIEWABLE_STATUSES.has(status);

const browserCanPlay = (mimeType: string): boolean => {
  if (typeof document === "undefined") return false;

  return document.createElement("video").canPlayType(mimeType) !== "";
};

const defaultOpenSectionsOf = (
  sections: ReturnType<typeof buildSections>,
  mode: "priority" | "all" | "none",
) => {
  if (mode === "none") return [];
  if (mode === "all") return sections;
  return sections.filter(isPrioritySection);
};
